<script setup lang="ts">
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import { computed, provide, watch } from 'vue';
import { RouterView } from 'vue-router';

// utils
import RetryAuthError from '~/errors/RetryAuthError';
// hooks
import useSearchParams from '~/hooks/useSearchParams';
// services
import keycloakApi from '~/services/keycloack-api/keycloack-api';
import useAuth from '~/store/auth/useAuth';
import useLogin from '~/store/auth/useLogin';
// store
// setPermissionCheck: atualiza o store global usado pela diretiva v-permission
import { setPermissionCheck } from '~/store/auth/usePermissionCheck';
import getErrorMessage from '~/utils/getErrorMessage';

import Spinner from './components/Spinner/Spinner.vue';
// layouts
import RootLayout from './layouts/RootLayout.vue';
import useLogout from './store/auth/useLogout';

const [searchParams] = useSearchParams();

const code = searchParams.value.get('code') as string | undefined;

const {
  needRefresh,
  updateServiceWorker,
} = useRegisterSW();

const {
  data: auth,
  error: authError,
  isError: isAuthError,
  isLoading: isAuthLoading,
  refetch: refetchAuth,
} = useAuth();

provide(
  'permissionCheck',
  computed(() => auth.value?.hasSomeRole ?? (() => false)),
);

watch(
  auth,
  (data) => {
    setPermissionCheck(data?.hasSomeRole);
  },
  { immediate: true },
);

const {
  error: loginError,
  isError: isLoginError,
  isPending: isLoginPending,
  mutateAsync: handleLogin,
} = useLogin();

const {
  isPending: isLogoutLoading,
  mutateAsync: handleLogout,
} = useLogout();

async function handleAuthError() {
  if (!code) return keycloakApi.redirect();

  return handleLogin(code)
    .catch(keycloakApi.redirect)
    .finally(keycloakApi.cleanUrlParams);
}

async function retry() {
  await updateServiceWorker();
  await refetchAuth();
}

watch([isAuthLoading, isAuthError], async () => {
  if (isAuthLoading.value || isAuthLoading.value) return;

  if (authError.value instanceof RetryAuthError) {
    await handleAuthError();
  }

  keycloakApi.cleanUrlParams();
});

const errorCode = computed(() => getErrorMessage([authError.value, loginError.value]));

</script>

<template>
  <VueQueryDevtools />

  <div v-if="needRefresh" class="centered">
    <Spinner />
    <p>Atualizando...</p>
  </div>

  <div v-if="isAuthLoading" class="centered">
    <Spinner />
    <p>Carregando sua sessão...</p>
  </div>

  <div v-else-if="isAuthError || isLoginError" class="centered">
    <template v-if="(authError instanceof RetryAuthError)">
      <Spinner />
      <p>Aguarde. Redirecionando...</p>
    </template>
    <template v-else>
      <h3>Fintech BackOffice</h3>
      <template v-if="errorCode === 'NOT_ADMIN'">
        <b>Você não tem permissão para acessar o sistema.</b> <br>
        <span>Entre em contato com o administrador.</span> <br><br>
        <button class="btn btn-md btn-primary" @click.prevent="() => handleLogout()" type="button">
          <span v-if="isLogoutLoading">Saindo...</span>
          <span v-else>Logout</span>
        </button>
      </template>
      <template v-else>
        <strong>Erro:</strong>
        <small>
          Código: {{ errorCode }}
        </small>
        <br>
        <button class="btn btn-md btn-primary" type="button" id="retry" size="medium" @click="retry">
          Tentar novamente
        </button>
      </template>
    </template>
  </div>

  <div v-else-if="isLoginPending" class="centered">
    <Loading />
    <p>Autenticando...</p>
  </div>

  <div v-else>
    <RootLayout>
      <RouterView />
    </RootLayout>
  </div>
</template>

<style lang="scss" scoped>
  .centered {
    display: flex;
    place-items: center;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100dvh;
    text-align: center;
    background-color: white;
    p {
      color: #333;
    }
  }
</style>
